// The task table shared by the Tasks page and the project page (Volkan,
// 2026-10-22, after monday.com): cell grid lines, a click on a column header
// sorts on it (asc → desc → usual order), a "Columns" menu hides columns, and
// the status is a coloured cell that opens the status choice.
import { Link } from '@tanstack/react-router'
import { ArrowDown, ArrowUp, Check, ChevronDown, ChevronRight, ChevronsUpDown } from 'lucide-react'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { DataTableShell } from '../../components/ui/data-table-shell'
import { FilterMenu, FilterMenuCheckbox } from '../../components/ui/filter-menu'
import { formatDateTime } from '../../lib/datetime'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import type { StaffTask, TaskSort, TaskStatus } from './api'
import { TASK_COLUMNS, nextSort, type SortState, type TaskColumn } from './columns'
import { DUE_BUCKETS, type DueBucket } from './dates'
import { assigneeNames, formatDay, projectRoute, taskDetailRoute, type TaskShell } from './lib'
import { PrivateLock } from './ProjectBits'

type Strings = (typeof staffTasksI18n)['en']

const GRID =
  '[&_th]:border [&_td]:border [&_th]:border-[var(--table-divider)] [&_td]:border-[var(--table-divider)] [&_tbody_tr]:border-t-0'

const STATUS_CELL: Record<TaskStatus, string> = {
  todo: 'bg-[var(--color-surface-muted)] text-[var(--color-text)]',
  in_progress: 'bg-[var(--color-warning)] text-white',
  done: 'bg-[var(--color-success)] text-white',
}

const BALANCED_COLUMN_WEIGHTS: Partial<Record<TaskColumn, number>> = {
  assignee: 18, due: 11, status: 12, project: 15, priority: 9, subtasks: 7,
  stage: 12, created_by: 12, created_at: 11,
}

function columnLabel(column: TaskColumn, t: Strings): string {
  switch (column) {
    case 'assignee':
      return t.columns.assignee
    case 'due':
      return t.columns.due
    case 'status':
      return t.columns.status
    case 'project':
      return t.table.columns.project
    case 'stage':
      return t.table.columns.stage
    case 'priority':
      return t.table.columns.priority
    case 'subtasks':
      return t.table.columns.subtasks
    case 'created_by':
      return t.table.columns.createdBy
    default:
      return t.table.columns.createdAt
  }
}

/** "Columns" menu: tick the columns to show. `omit` ones are not offered. */
export function ColumnsMenu({
  hidden,
  onToggle,
  omit = [],
}: {
  hidden: TaskColumn[]
  onToggle: (column: TaskColumn, shown: boolean) => void
  omit?: TaskColumn[]
}) {
  const t = useT(staffTasksI18n)
  const offered = TASK_COLUMNS.filter((c) => !omit.includes(c.key))
  return (
    <FilterMenu
      label={t.table.columnsMenu}
      activeCount={offered.filter((c) => hidden.includes(c.key)).length}
    >
      {() => (
        <div data-testid="staff-task-columns-menu">
          {offered.map((column) => (
            <FilterMenuCheckbox
              key={column.key}
              checked={!hidden.includes(column.key)}
              onChange={(shown) => onToggle(column.key, shown)}
            >
              {columnLabel(column.key, t)}
            </FilterMenuCheckbox>
          ))}
        </div>
      )}
    </FilterMenu>
  )
}

type Props = {
  shell: TaskShell
  items: StaffTask[]
  hidden: TaskColumn[]
  /** Columns that make no sense on this screen (e.g. Project on a project page). */
  omit?: TaskColumn[]
  sort: SortState
  onSort: (next: SortState) => void
  onStatus: (task: StaffTask, next: TaskStatus) => void
  onEditAssignees?: (task: StaffTask) => void
  /** One-tap "Done" in front of an open task's title; left out when not allowed. */
  onDone?: (task: StaffTask) => void
  /** Due-date groups (My tasks, one person); sorting then applies inside each group. */
  groups?: Record<DueBucket, StaffTask[]>
  collapsed?: Set<DueBucket>
  onToggleGroup?: (bucket: DueBucket) => void
  busy?: boolean
  testId?: string
  balancedColumns?: boolean
}

export function TaskTable({
  shell,
  items,
  hidden,
  omit = [],
  sort,
  onSort,
  onStatus,
  onEditAssignees,
  onDone,
  groups,
  collapsed,
  onToggleGroup,
  busy,
  testId = 'staff-task-table',
  balancedColumns = false,
}: Props) {
  const t = useT(staffTasksI18n)
  const columns = TASK_COLUMNS.filter((c) => !hidden.includes(c.key) && !omit.includes(c.key))
  const visibleWeight = columns.reduce((total, column) => total + (BALANCED_COLUMN_WEIGHTS[column.key] ?? 11), 0)
  const span = columns.length + 1
  const row = (task: StaffTask) => (
    <TaskRow
      key={task.id}
      task={task}
      shell={shell}
      columns={columns.map((c) => c.key)}
      onStatus={(next) => onStatus(task, next)}
      onEditAssignees={onEditAssignees ? () => onEditAssignees(task) : undefined}
      onDone={onDone ? () => onDone(task) : undefined}
    />
  )

  return (
    <DataTableShell aria-busy={busy} className={GRID}>
      {/* The task column keeps room to read; the table scrolls sideways instead. */}
      <table data-testid={testId} style={{ minWidth: balancedColumns ? `${Math.max(60, 18 + columns.length * 7)}rem` : `${18 + columns.length * 9}rem` }}>
        <thead>
          <tr>
            <SortHeader
              label={t.columns.task}
              sortKey="title"
              sort={sort}
              onSort={onSort}
              className={balancedColumns ? undefined : 'min-w-[18rem]'}
              width={balancedColumns ? '31%' : undefined}
            />
            {columns.map((column) => (
              <SortHeader
                key={column.key}
                label={columnLabel(column.key, t)}
                sortKey={column.sort}
                sort={sort}
                onSort={onSort}
                className={balancedColumns ? undefined : 'w-36'}
                width={balancedColumns ? `${(BALANCED_COLUMN_WEIGHTS[column.key] ?? 11) / visibleWeight * 69}%` : undefined}
              />
            ))}
          </tr>
        </thead>
        {groups ? (
          DUE_BUCKETS.filter((bucket) => groups[bucket].length > 0).map((bucket) => {
            const open = !collapsed?.has(bucket)
            return (
              <tbody key={bucket} data-testid={`staff-task-group-${bucket}`}>
                <tr className="!h-auto !bg-[var(--table-header-bg)]">
                  <td colSpan={span} className="py-1.5">
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => onToggleGroup?.(bucket)}
                      className={cn(
                        'inline-flex items-center gap-1 text-sm font-semibold',
                        bucket === 'overdue'
                          ? 'text-[var(--color-danger)]'
                          : 'text-[var(--color-text)]',
                      )}
                    >
                      {open ? (
                        <ChevronDown className="h-4 w-4" aria-hidden />
                      ) : (
                        <ChevronRight className="h-4 w-4" aria-hidden />
                      )}
                      {t.buckets[bucket]}
                      <span className="font-normal text-[var(--color-text-muted)]">
                        · {groups[bucket].length}
                      </span>
                    </button>
                  </td>
                </tr>
                {open && groups[bucket].map(row)}
              </tbody>
            )
          })
        ) : (
          <tbody>{items.map(row)}</tbody>
        )}
      </table>
    </DataTableShell>
  )
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  className,
  width,
}: {
  label: string
  sortKey?: TaskSort
  sort: SortState
  onSort: (next: SortState) => void
  className?: string
  width?: string
}) {
  const t = useT(staffTasksI18n)
  if (!sortKey) return <th className={className} style={width ? { width } : undefined}>{label}</th>
  const active = sort?.key === sortKey ? sort.dir : null
  const Icon = active === 'asc' ? ArrowUp : active === 'desc' ? ArrowDown : ChevronsUpDown
  return (
    <th
      className={className}
      style={width ? { width } : undefined}
      aria-sort={active === 'asc' ? 'ascending' : active === 'desc' ? 'descending' : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(nextSort(sort, sortKey))}
        aria-label={t.table.sortBy(label)}
        className="inline-flex w-full items-center gap-1 text-left font-semibold hover:text-[var(--color-text)]"
      >
        <span className="truncate">{label}</span>
        <Icon
          className={cn('h-3.5 w-3.5 shrink-0', active ? 'opacity-100' : 'opacity-40')}
          aria-hidden
        />
      </button>
    </th>
  )
}

function TaskRow({
  task,
  shell,
  columns,
  onStatus,
  onEditAssignees,
  onDone,
}: {
  task: StaffTask
  shell: TaskShell
  columns: TaskColumn[]
  onStatus: (next: TaskStatus) => void
  onEditAssignees?: () => void
  onDone?: () => void
}) {
  const t = useT(staffTasksI18n)
  const showSubtasks = columns.includes('subtasks')
  const showPriority = columns.includes('priority')
  return (
    <tr data-testid={`staff-task-row-${task.id}`}>
      <td className="py-2">
        <div className="flex items-start gap-2">
          {onDone && task.status !== 'done' && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              title={t.quick.markDone(task.title)}
              aria-label={t.quick.markDone(task.title)}
              onClick={onDone}
              className="shrink-0 rounded-full border border-[var(--input-border)] hover:border-[var(--color-success)] hover:text-[var(--color-success)] sm:h-7 sm:min-h-7 sm:w-7 sm:min-w-7"
              data-testid={`staff-task-done-${task.id}`}
            >
              <Check className="h-4 w-4" aria-hidden />
            </Button>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                {...taskDetailRoute(shell, task.id)}
                className={cn(
                  'font-medium text-[var(--color-text)] hover:underline',
                  task.status === 'done' && 'text-[var(--color-text-muted)] line-through',
                )}
              >
                {task.title}
              </Link>
              {task.priority === 'urgent' && !showPriority && (
                <Badge variant="destructive">{t.priority.urgent}</Badge>
              )}
              {task.repeat_rule && (
                <Badge variant="neutral" data-testid="staff-task-repeat">
                  {t.repeat.badge(t.repeat.rules[task.repeat_rule])}
                </Badge>
              )}
            </div>
            {(task.link ||
              task.comment_count > 0 ||
              (task.subtasks_total > 0 && !showSubtasks) ||
              task.attachment_count > 0) && (
              <div className="mt-0.5 flex flex-wrap gap-3 text-xs text-[var(--color-text-muted)]">
                {task.link && (
                  <span>
                    {t.linked.kinds[task.link.kind]} · {task.link.label}
                  </span>
                )}
                {task.subtasks_total > 0 && !showSubtasks && (
                  <span data-testid="staff-task-subtask-count">
                    {t.structure.subtasks}{' '}
                    {t.structure.subtaskProgress(task.subtasks_done, task.subtasks_total)}
                  </span>
                )}
                {task.comment_count > 0 && <span>{t.comments(task.comment_count)}</span>}
                {task.attachment_count > 0 && (
                  <span>{t.structure.fileCount(task.attachment_count)}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </td>
      {columns.map((column) => (
        <Cell key={column} column={column} task={task} shell={shell} onStatus={onStatus} onEditAssignees={onEditAssignees} />
      ))}
    </tr>
  )
}

const MUTED = 'text-[var(--color-text-muted)]'

function Cell({
  column,
  task,
  shell,
  onStatus,
  onEditAssignees,
}: {
  column: TaskColumn
  task: StaffTask
  shell: TaskShell
  onStatus: (next: TaskStatus) => void
  onEditAssignees?: () => void
}) {
  const t = useT(staffTasksI18n)
  switch (column) {
    case 'assignee':
      return (
        <td className="text-sm">
          {onEditAssignees ? (
            <button
              type="button"
              onClick={onEditAssignees}
              aria-label={t.table.editAssigneesFor(task.title)}
              title={task.assignees.map((person) => person.display_name).join(', ') || t.unassigned}
              className="staff-task-assignee-trigger inline-flex min-h-9 max-w-full items-center gap-2 rounded-md px-2 text-left text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)]"
            >
              <span className="staff-task-assignee-avatar flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-muted)] text-[11px] font-semibold text-[var(--color-text-muted)]" aria-hidden>
                {task.assignees[0]?.display_name.slice(0, 1).toUpperCase() ?? '+'}
              </span>
              <span className="min-w-0 line-clamp-2 break-words">{task.assignees.length > 0 ? assigneeNames(task.assignees, t.structure.more) : t.unassigned}</span>
            </button>
          ) : task.assignees.length > 0 ? (
            <span title={task.assignees.map((person) => person.display_name).join(', ')}>
              {assigneeNames(task.assignees, t.structure.more)}
            </span>
          ) : (
            <span className={MUTED}>{t.unassigned}</span>
          )}
        </td>
      )
    case 'due': {
      const due = formatDay(task.due_date)
      return (
        <td className="text-sm">
          {due ? (
            <span className={cn(task.is_overdue && 'font-medium text-[var(--color-danger)]')}>
              {due}
              {task.is_overdue && <span className="sr-only"> ({t.overdue})</span>}
            </span>
          ) : (
            <span className={MUTED}>{t.noDueDate}</span>
          )}
        </td>
      )
    }
    case 'status':
      return (
        <td className="relative w-36 !p-0">
          <select
            aria-label={`${t.columns.status}: ${task.title}`}
            value={task.status}
            onChange={(e) => onStatus(e.target.value as TaskStatus)}
            className={cn(
              'absolute inset-0 h-full w-full cursor-pointer appearance-none border-0 px-2 text-center text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-text)]',
              STATUS_CELL[task.status],
            )}
            data-testid={`staff-task-status-${task.id}`}
          >
            <option value="todo">{t.status.todo}</option>
            <option value="in_progress">{t.status.in_progress}</option>
            <option value="done">{t.status.done}</option>
          </select>
        </td>
      )
    case 'project':
      return (
        <td className="text-sm">
          {task.project ? (
            <Link {...projectRoute(shell, task.project.id)} className="hover:underline">
              {task.project.name}
              {task.project.is_private && (
                <PrivateLock className="ml-1 inline h-3 w-3 align-[-1px]" />
              )}
            </Link>
          ) : (
            <span className={MUTED}>—</span>
          )}
        </td>
      )
    case 'stage':
      return <td className="text-sm">{task.stage_name ?? <span className={MUTED}>—</span>}</td>
    case 'priority':
      return (
        <td className="text-sm">
          {task.priority === 'urgent' ? (
            <Badge variant="destructive">{t.priority.urgent}</Badge>
          ) : (
            <span className={MUTED}>{t.priority.normal}</span>
          )}
        </td>
      )
    case 'subtasks':
      return (
        <td className="text-sm">
          {task.subtasks_total > 0 ? (
            <span className="flex items-center gap-2 tabular-nums" data-testid="staff-task-subtask-count">
              <span className="h-1.5 w-12 shrink-0 overflow-hidden rounded-full bg-[var(--color-surface-muted)]">
                <span
                  className="block h-full rounded-full bg-[var(--color-success)]"
                  style={{ width: `${Math.round((task.subtasks_done / task.subtasks_total) * 100)}%` }}
                />
              </span>
              {t.structure.subtaskProgress(task.subtasks_done, task.subtasks_total)}
            </span>
          ) : (
            <span className={MUTED}>—</span>
          )}
        </td>
      )
    case 'created_by':
      return (
        <td className="text-sm">
          {task.created_by?.display_name ?? <span className={MUTED}>—</span>}
        </td>
      )
    default:
      return (
        <td className="text-sm">
          {formatDateTime(task.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}
        </td>
      )
  }
}
